import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import type { ChartOptions } from 'chart.js';
import { Radar } from 'react-chartjs-2';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// Color palette for multiple datasets
const CHART_COLORS = [
  { bg: 'rgba(99, 102, 241, 0.2)', border: 'rgb(99, 102, 241)' },   // indigo
  { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgb(239, 68, 68)' },     // red
  { bg: 'rgba(34, 197, 94, 0.2)', border: 'rgb(34, 197, 94)' },     // green
  { bg: 'rgba(234, 179, 8, 0.2)', border: 'rgb(234, 179, 8)' },     // yellow
  { bg: 'rgba(168, 85, 247, 0.2)', border: 'rgb(168, 85, 247)' },   // purple
  { bg: 'rgba(236, 72, 153, 0.2)', border: 'rgb(236, 72, 153)' },   // pink
  { bg: 'rgba(14, 165, 233, 0.2)', border: 'rgb(14, 165, 233)' },   // sky
  { bg: 'rgba(249, 115, 22, 0.2)', border: 'rgb(249, 115, 22)' },   // orange
  { bg: 'rgba(20, 184, 166, 0.2)', border: 'rgb(20, 184, 166)' },   // teal
  { bg: 'rgba(163, 163, 163, 0.2)', border: 'rgb(163, 163, 163)' }  // gray
];

// Same emotion groups as admin_analysis.html
const SKILL_GROUPS: Record<string, string[]> = {
  'COMMITTED': ['motivated', 'enthusiastic', 'excited', 'engaged', 'confident', 'supportive', 'collaborative', 'assertive', 'decisive', 'inclusive', 'empowered', 'proactive'],
  'UPBEAT': ['happy', 'optimistic', 'proud', 'hopeful', 'interested', 'reassured', 'trusting', 'inspired', 'determined', 'jovial', 'playful', 'constructive'],
  'CONTENT': ['satisfied', 'grateful', 'appreciative', 'curious', 'relieved', 'forgiving', 'considerate', 'reflective', 'observant', 'adaptable', 'flexible', 'empathetic'],
  'COOL': ['calm', 'relaxed', 'compassionate', 'understanding', 'respectful', 'neutral', 'cautious', 'mindful', 'diligent', 'meticulous', 'open_minded', 'ironic'],
  'DRAINED': ['stressed', 'indifferent', 'overwhelmed', 'withdrawn', 'isolated', 'demotivated', 'discouraged', 'exhausted', 'burnt_out', 'disempowered', 'hurt', 'resentful'],
  'DOWNBEAT': ['bored', 'concerned', 'doubtful', 'hesitant', 'confused', 'uncertain', 'ignored', 'sad', 'distracted', 'apologetic', 'disappointed', 'undervalued'],
  'TROUBLED': ['anxious', 'resistant', 'insecure', 'disrespectful', 'surprised', 'critical', 'embarrassed', 'stubborn', 'suspicious', 'skeptical', 'judgmental', 'exclusive'],
  'EXASPERATED': ['frustrated', 'annoyed', 'irritated', 'defensive', 'angry', 'shocked', 'impatient', 'betrayed', 'sarcastic', 'passive_aggressive', 'dismissive', 'reactive']
};

const LABELS = Object.keys(SKILL_GROUPS);

interface EmotionData {
  [key: string]: number;
}

interface AnalysisItem {
  id: string;
  emotion?: EmotionData;
}

interface CommunicationRadarProps {
  analyses: AnalysisItem[];
}

/**
 * Calculate average scores for each emotional category from emotion data
 */
function calculateAverageScores(emotionData: EmotionData): number[] {
  return LABELS.map(group => {
    const skillsInGroup = SKILL_GROUPS[group];
    let total = 0;
    let count = 0;
    
    skillsInGroup.forEach(skill => {
      const key = `${skill}_rating`;
      if (emotionData[key] !== undefined) {
        total += Number(emotionData[key]) || 0;
        count += 1;
      }
    });
    
    return count > 0 ? (total / count) : 0;
  });
}

export const CommunicationRadar: React.FC<CommunicationRadarProps> = ({ analyses }) => {
  const [displayMode, setDisplayMode] = useState<'average' | 'all'>('average');

  const chartData = useMemo(() => {
    if (displayMode === 'average') {
      // Calculate average across all analyses
      const averageData = new Array(LABELS.length).fill(0);
      const counts = new Array(LABELS.length).fill(0);

      analyses.forEach(analysis => {
        if (analysis.emotion) {
          const scores = calculateAverageScores(analysis.emotion);
          scores.forEach((score, idx) => {
            if (score > 0) {
              averageData[idx] += score;
              counts[idx] += 1;
            }
          });
        }
      });

      // Compute final averages
      const finalData = averageData.map((sum, idx) => 
        counts[idx] > 0 ? sum / counts[idx] : 0
      );

      return {
        labels: LABELS,
        datasets: [
          {
            label: 'Average Communication Pattern',
            data: finalData,
            backgroundColor: 'rgba(99, 102, 241, 0.2)', // indigo-500 with opacity
            borderColor: 'rgb(99, 102, 241)',
            pointBackgroundColor: 'rgb(99, 102, 241)',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: 'rgb(99, 102, 241)',
            borderWidth: 2,
          }
        ]
      };
    } else {
      // Show all analyses (limit to 10 most recent)
      const recentAnalyses = analyses.slice(0, 10);
      const datasets = recentAnalyses.map((analysis, index) => {
        const scores = analysis.emotion 
          ? calculateAverageScores(analysis.emotion)
          : new Array(LABELS.length).fill(0);
        
        const colorIndex = index % CHART_COLORS.length;
        const colors = CHART_COLORS[colorIndex];
        
        return {
          label: `Analysis ${index + 1}`,
          data: scores,
          backgroundColor: colors.bg,
          borderColor: colors.border,
          pointBackgroundColor: colors.border,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: colors.border,
          borderWidth: 2,
        };
      });

      return {
        labels: LABELS,
        datasets
      };
    }
  }, [analyses, displayMode]);

  const options: ChartOptions<'radar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        pointLabels: {
          font: {
            size: 13,
            weight: 500,
          },
          color: '#4A5568',
        },
        ticks: {
          backdropColor: 'rgba(255, 255, 255, 0.75)',
          color: '#718096',
          stepSize: 0.2,
        },
        suggestedMin: 0,
        suggestedMax: 1,
      }
    },
    plugins: {
      legend: {
        display: displayMode === 'all',
        position: 'bottom',
        labels: {
          font: {
            size: 11,
          },
          padding: 10,
          usePointStyle: true,
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.r.toFixed(2)}`;
          }
        }
      }
    }
  };

  if (analyses.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-500">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="mt-2">No analyses yet</p>
          <p className="text-sm text-gray-400">Analyse some communications to see your patterns</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Toggle controls */}
      <div className="absolute top-0 right-0 z-10 flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1 shadow-sm">
        <button
          onClick={() => setDisplayMode('average')}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
            displayMode === 'average'
              ? 'bg-indigo-600 text-white'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Average
        </button>
        <button
          onClick={() => setDisplayMode('all')}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
            displayMode === 'all'
              ? 'bg-indigo-600 text-white'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          All
        </button>
      </div>
      
      {/* Chart */}
      <div className={displayMode === 'all' ? 'h-[28rem]' : 'h-96'}>
        <Radar data={chartData} options={options} />
      </div>
    </div>
  );
};

